import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Link, Routes, Route } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  ChatCircleText,
  CheckCircle,
  Code,
  Database,
  DeviceMobile,
  EnvelopeSimple,
  FacebookLogo,
  GlobeHemisphereWest,
  InstagramLogo,
  LinkedinLogo,
  MapPin,
  RocketLaunch,
  ShieldCheck,
  ShoppingCartSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "@/components/ui/sonner";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import IPolliniPrivacyPolicyPage from "@/pages/IPolliniPrivacyPolicyPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import SalieriCircusPrivacyPolicyPage from "@/pages/SalieriCircusPrivacyPolicyPage";
import SalieriCircusPrivacyPolicyPageEn from "@/pages/SalieriCircusPrivacyPolicyPageEn";
import SitePrivacyPolicyPage from "@/pages/SitePrivacyPolicyPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  {
    id: "sviluppo-software",
    title: "Sviluppo software",
    text: "Applicazioni e gestionali costruiti intorno ai processi reali della tua azienda.",
    icon: Code,
    span: "lg:col-span-2",
  },
  {
    id: "pagine-web",
    title: "Pagine web",
    text: "Siti veloci, chiari e memorabili per aziende e professionisti che vogliono presentarsi meglio.",
    icon: GlobeHemisphereWest,
    span: "",
  },
  {
    id: "chatbot-ai",
    title: "Chatbot AI",
    text: "Assistenti conversazionali addestrati sui tuoi dati per alleggerire customer service e vendite.",
    icon: ChatCircleText,
    span: "",
  },
  {
    id: "app-mobile",
    title: "App mobile",
    text: "Esperienze iOS e Android intuitive, pronte per utenti, team interni e community digitali.",
    icon: DeviceMobile,
    span: "",
  },
  {
    id: "ecommerce",
    title: "E-commerce",
    text: "Piattaforme di vendita complete, scalabili e pensate per convertire visite in ordini.",
    icon: ShoppingCartSimple,
    span: "lg:col-span-2",
  },
];

const formInitialState = {
  name: "",
  email: "",
  phone: "",
  service_requested: "",
  message: "",
  privacy_consent: false,
  captcha_answer: "",
  website: "",
};

const getValidationMessage = (data, showCaptcha, captchaChallenge) => {
  if (data.name.trim().length < 2) return "Inserisci un nome valido.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) return "Inserisci un indirizzo email valido.";
  if (data.phone.trim() && data.phone.trim().length < 5) return "Inserisci un numero di telefono valido oppure lascia il campo vuoto.";
  if (!data.service_requested) return "Seleziona il servizio richiesto.";
  if (data.message.trim().length < 10) return "Inserisci un messaggio di almeno 10 caratteri.";
  if (!data.privacy_consent) return "Autorizza il trattamento dei dati personali per essere ricontattato.";
  if (showCaptcha && !captchaChallenge?.id) return "Verifica anti-spam non pronta. Attendi un istante.";
  if (showCaptcha && !data.captcha_answer.trim()) return "Completa la verifica anti-spam.";
  return "";
};

const philosophy = [
  {
    number: "0",
    label: "sforzo per il Cliente",
    copy: "Ti guidiamo dalla prima idea al rilascio, con un processo semplice e leggibile.",
  },
  {
    number: "5",
    label: "ambiti di eccellenza",
    copy: "Software, web, AI, app mobile ed e-commerce in un unico team operativo.",
  },
  {
    number: "0",
    label: "compromessi sulla qualità",
    copy: "Ogni scelta tecnica punta a sicurezza, solidità e crescita nel tempo.",
  },
];

const caseStudies = [
  {
    id: "businessprod",
    name: "BusinessProd",
    sector: "Produzione materie plastiche",
    problem: "Bemar aveva bisogno di un sistema produttivo più aderente ai flussi operativi attuali.",
    solution: "È stata realizzata una versione specifica di MES, sincronizzata con il gestionale per anagrafiche, ordini clienti e fornitori, con funzioni Industry 4.0 verso macchine compatibili.",
    result: "Gestione commesse, stampe etichette e documenti di produzione integrate in un unico flusso operativo.",
    tech: ".NET, SQL Server, architettura client/server locale",
    icon: Database,
  },
  {
    id: "gestione-offerte",
    name: "Gestione Offerte",
    sector: "Arredo e gestione commerciale",
    problem: "Manerba aveva necessità di creare, condividere e uniformare offerte economiche personalizzate per i clienti.",
    solution: "Sviluppo di un sistema collegato a SQL Azure e sincronizzato con gestionale AS400, con import anagrafiche, scontistiche, impaginazioni e foto prodotto.",
    result: "Processo offerte più strutturato, documenti commerciali completi e dati condivisi tra gestionale e team.",
    tech: ".NET, DevExpress, SQL Azure, sincronizzazione AS400",
    icon: Code,
  },
  {
    id: "businesslog",
    name: "BusinessLog",
    sector: "Sicurezza, log management e SOC",
    problem: "Serviva centralizzare access-log e altri eventi da reti Windows, server, device e piattaforme cloud.",
    solution: "Applicazione agent-less con database centralizzato, connettori per Windows, Linux/Unix, Mac, firewall, NAS, software di teleassistenza, Azure e Office 365.",
    result: "Dashboard riepilogativa con supporto IA per evidenziare gli eventi principali e quelli da attenzionare dal SOC interno.",
    tech: "Log collection agent-less, connettori multi-sorgente, dashboard IA",
    icon: ShieldCheck,
  },
  {
    id: "salieri-circus-award",
    name: "App Salieri Circus Award",
    sector: "Evento artistico e intrattenimento",
    problem: "L’organizzazione aveva bisogno di un’app ufficiale per pubblico, contenuti evento e gestione operativa in sala.",
    solution: "App con gallerie foto/video, news, calendario eventi, votazioni del pubblico, area VIP e buoni pasto tramite QR code.",
    result: "Applicazione pubblicata su Apple Store e Google Play, con funzioni dedicate a spettatori, staff e artisti.",
    tech: "App mobile iOS/Android, QR code, gestione contenuti evento",
    icon: DeviceMobile,
  },
];

const Home = () => {
  const [formData, setFormData] = useState(formInitialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedLead, setSubmittedLead] = useState(null);
  const [submissionError, setSubmissionError] = useState("");
  const [captchaChallenge, setCaptchaChallenge] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [formSession, setFormSession] = useState(null);
  const [pointerStyle, setPointerStyle] = useState({ "--mouse-x": "50%", "--mouse-y": "18%" });

  const loadContactSession = async () => {
    try {
      const response = await axios.get(`${API}/contact-session`);
      setFormSession(response.data);
    } catch (error) {
      setFormSession(null);
    }
  };

  const loadCaptcha = async () => {
    try {
      const response = await axios.get(`${API}/captcha`);
      setCaptchaChallenge(response.data);
      setFormData((current) => ({ ...current, captcha_answer: "", website: "" }));
    } catch (error) {
      setCaptchaChallenge(null);
      setSubmissionError("Non siamo riusciti a caricare la verifica anti-spam. Ricarica la pagina.");
    }
  };

  useEffect(() => {
    loadContactSession();
  }, []);

  const handleFieldChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmittedLead(null);
    setSubmissionError("");

    const elapsedSeconds = formSession?.started_at
      ? (Date.now() - new Date(formSession.started_at).getTime()) / 1000
      : 0;

    if (!showCaptcha && (!formSession || elapsedSeconds < (formSession.min_seconds || 2))) {
      setShowCaptcha(true);
      await loadCaptcha();
      const message = "Per sicurezza completa la verifica anti-spam.";
      setSubmissionError(message);
      toast.error(message);
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      message: formData.message.trim(),
      privacy_consent: formData.privacy_consent,
      captcha_answer: formData.captcha_answer.trim(),
      website: formData.website.trim(),
      captcha_id: showCaptcha ? (captchaChallenge?.id || "") : "",
      form_session_id: formSession?.id || "",
      form_token: formSession?.token || "",
      form_started_at: formSession?.started_at || new Date().toISOString(),
    };
    const validationMessage = getValidationMessage(payload, showCaptcha, captchaChallenge);

    if (validationMessage) {
      setSubmissionError(validationMessage);
      toast.error(validationMessage);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, payload);
      setSubmittedLead(response.data);
      setFormData(formInitialState);
      setShowCaptcha(false);
      setCaptchaChallenge(null);
      await loadContactSession();
      toast.success("Richiesta salvata. Ti ricontatteremo al più presto.");
    } catch (error) {
      const requiresCaptcha = error?.response?.status === 403 && error?.response?.data?.detail?.requires_captcha;
      if (requiresCaptcha) {
        setShowCaptcha(true);
        await loadCaptcha();
      } else if (showCaptcha) {
        await loadCaptcha();
      }
      const isValidationError = error?.response?.status === 422 || error?.response?.status === 400;
      const detail = error?.response?.data?.detail;
      const message = isValidationError
        ? (typeof detail === "string" ? detail : "Controlla i campi inseriti: uno o più valori non sono validi.")
        : requiresCaptcha
          ? (detail?.message || "Per sicurezza completa la verifica anti-spam.")
        : "Non siamo riusciti a inviare la richiesta. Riprova tra poco.";
      setSubmissionError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePointerMove = (event) => {
    const x = `${Math.round((event.clientX / window.innerWidth) * 100)}%`;
    const y = `${Math.round((event.clientY / window.innerHeight) * 100)}%`;
    setPointerStyle({ "--mouse-x": x, "--mouse-y": y });
  };

  return (
    <div className="app-shell min-h-screen overflow-x-hidden bg-[#050505] text-[#F5F5F7]" style={pointerStyle} onPointerMove={handlePointerMove} data-testid="interactive-background-shell">
      <div className="reactive-background" data-testid="reactive-background-layer" aria-hidden="true">
        <span className="reactive-glow reactive-glow-primary" />
        <span className="reactive-glow reactive-glow-accent" />
        <span className="reactive-scanline" />
      </div>
      <Toaster position="top-right" richColors />
      <nav className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl" data-testid="main-navigation">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8 lg:px-12">
          <a href="#top" className="group flex items-center gap-3" data-testid="nav-logo-link" aria-label="ZeroCinqueZero home">
            <span className="grid h-10 w-10 place-items-center border border-white/15 bg-white text-sm font-black text-black transition-transform duration-300 group-hover:-translate-y-0.5" data-testid="nav-logo-mark">
              050
            </span>
            <span className="hidden text-sm font-semibold uppercase tracking-[0.24em] text-white sm:inline" data-testid="nav-brand-name">
              ZeroCinqueZero
            </span>
          </a>
          <div className="hidden items-center gap-8 text-sm text-[#8B8B96] md:flex" data-testid="nav-links">
            <a href="#metodo" className="transition-colors duration-300 hover:text-white" data-testid="nav-method-link">Metodo</a>
            <a href="#servizi" className="transition-colors duration-300 hover:text-white" data-testid="nav-services-link">Servizi</a>
            <a href="#casi-studio" className="transition-colors duration-300 hover:text-white" data-testid="nav-case-studies-link">Casi studio</a>
            <a href="#contatti" className="transition-colors duration-300 hover:text-white" data-testid="nav-contact-link">Contatti</a>
          </div>
          <Button asChild className="rounded-none bg-[#0047FF] px-5 text-white shadow-[0_0_28px_rgba(0,71,255,0.35)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#336BFF] hover:shadow-[0_0_42px_rgba(0,71,255,0.5)]" data-testid="nav-cta-button">
            <a href="#contatti">Parliamone</a>
          </Button>
        </div>
      </nav>

      <main id="top">
        <section className="relative isolate min-h-screen overflow-hidden px-5 pt-32 sm:px-8 lg:px-12" data-testid="hero-section">
          <div className="hero-grid absolute inset-0 -z-10" aria-hidden="true" />
          <div className="hero-orb hero-orb-blue" aria-hidden="true" />
          <div className="hero-orb hero-orb-red" aria-hidden="true" />
          <div className="mx-auto grid max-w-7xl items-center gap-14 pb-20 pt-12 lg:grid-cols-[1.12fr_0.88fr] lg:pb-28 lg:pt-20">
            <div className="max-w-4xl" data-testid="hero-copy-block">
              <p className="reveal mb-6 inline-flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#8B8B96]" data-testid="hero-eyebrow">
                <RocketLaunch size={18} weight="fill" className="text-[#0047FF]" /> Software house italiana
              </p>
              <h1 className="reveal reveal-delay-1 max-w-5xl text-5xl font-black leading-[0.88] tracking-[-0.07em] text-white sm:text-6xl lg:text-7xl xl:text-8xl" data-testid="hero-title">
                Soluzioni digitali su misura, pensate per durare.
              </h1>
              <p className="reveal reveal-delay-2 mt-8 max-w-2xl text-base leading-8 text-[#B8B8C2] md:text-lg" data-testid="hero-description">
                ZeroCinqueZero progetta software, siti web, chatbot, app mobile ed e-commerce con un approccio concreto: tecnologia chiara, sicura e scalabile per semplificare il lavoro della tua azienda.
              </p>
              <div className="reveal reveal-delay-3 mt-10 flex flex-col gap-4 sm:flex-row" data-testid="hero-actions">
                <Button asChild className="group h-12 rounded-none bg-[#0047FF] px-7 text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#336BFF] hover:shadow-[0_0_38px_rgba(0,71,255,0.45)]" data-testid="hero-primary-cta">
                  <a href="#contatti">Raccontaci il progetto <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></a>
                </Button>
                <Button asChild variant="outline" className="h-12 rounded-none border-white/15 bg-transparent px-7 text-white transition-[border-color,transform,background-color] duration-300 hover:-translate-y-1 hover:border-white/35 hover:bg-white/10" data-testid="hero-secondary-cta">
                  <a href="#servizi">Scopri i servizi</a>
                </Button>
              </div>
            </div>

            <div className="reveal reveal-delay-2 relative" data-testid="hero-visual-card">
              <div className="relative overflow-hidden border border-white/10 bg-[#0F0F13]/75 p-5 shadow-2xl backdrop-blur-xl">
                <div className="mb-8 flex items-center justify-between border-b border-white/10 pb-4" data-testid="hero-terminal-header">
                  <span className="text-xs uppercase tracking-[0.3em] text-[#8B8B96]">Build log</span>
                  <span className="h-2.5 w-2.5 rounded-full bg-[#0047FF] shadow-[0_0_24px_#0047FF]" />
                </div>
                <div className="space-y-5 font-mono text-sm" data-testid="hero-terminal-lines">
                  <div className="flex items-center gap-3 text-[#8B8B96]" data-testid="terminal-line-discovery"><CheckCircle className="text-[#0047FF]" weight="fill" /> Analisi processi aziendali</div>
                  <div className="flex items-center gap-3 text-[#8B8B96]" data-testid="terminal-line-architecture"><CheckCircle className="text-[#0047FF]" weight="fill" /> Architettura solida e sicura</div>
                  <div className="flex items-center gap-3 text-[#8B8B96]" data-testid="terminal-line-release"><CheckCircle className="text-[#0047FF]" weight="fill" /> Rilascio scalabile</div>
                </div>
                <div className="mt-10 grid grid-cols-2 gap-3" data-testid="hero-metrics-grid">
                  <div className="border border-white/10 bg-white/[0.03] p-5" data-testid="hero-metric-zero-effort">
                    <p className="text-4xl font-black tracking-[-0.08em] text-white">0</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.18em] text-[#8B8B96]">Sforzo superfluo</p>
                  </div>
                  <div className="border border-white/10 bg-[#0047FF] p-5" data-testid="hero-metric-five-services">
                    <p className="text-4xl font-black tracking-[-0.08em] text-white">5</p>
                    <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/80">Ambiti digitali</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="metodo" className="px-5 py-24 sm:px-8 sm:py-32 lg:px-12" data-testid="philosophy-section">
          <div className="mx-auto max-w-7xl">
            <div className="mb-14 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end" data-testid="philosophy-heading-block">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#0047FF]" data-testid="philosophy-eyebrow">Il nostro 0-5-0</p>
              <h2 className="text-4xl font-black leading-[0.95] tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-testid="philosophy-title">
                Meno attrito. Più competenze. Nessun compromesso.
              </h2>
            </div>
            <div className="grid gap-4 md:grid-cols-3" data-testid="philosophy-grid">
              {philosophy.map((item, index) => (
                <article key={item.label} className="group border border-white/10 bg-[#0F0F13]/80 p-7 transition-[border-color,transform,background-color] duration-300 hover:-translate-y-1 hover:border-[#0047FF]/70 hover:bg-[#12121A]" data-testid={`philosophy-card-${index + 1}`}>
                  <span className="block text-[7rem] font-black leading-none tracking-[-0.12em] text-white/95 transition-colors duration-300 group-hover:text-[#0047FF]" data-testid={`philosophy-number-${index + 1}`}>{item.number}</span>
                  <h3 className="mt-6 text-xl font-bold text-white" data-testid={`philosophy-label-${index + 1}`}>{item.label}</h3>
                  <p className="mt-4 text-sm leading-7 text-[#8B8B96]" data-testid={`philosophy-copy-${index + 1}`}>{item.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="servizi" className="px-5 py-24 sm:px-8 sm:py-32 lg:px-12" data-testid="services-section">
          <div className="mx-auto max-w-7xl">
            <div className="mb-16 max-w-3xl" data-testid="services-heading-block">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#FF2E00]" data-testid="services-eyebrow">I nostri servizi</p>
              <h2 className="mt-5 text-4xl font-black leading-[0.95] tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-testid="services-title">
                Un solo team, più competenze digitali.
              </h2>
              <p className="mt-6 text-base leading-8 text-[#B8B8C2]" data-testid="services-description">
                Dallo sviluppo web alla cybersecurity applicativa, dall’AI conversazionale alle piattaforme di vendita: costruiamo strumenti che restano semplici da usare anche quando fanno cose complesse.
              </p>
            </div>
            <div className="grid auto-rows-fr gap-4 lg:grid-cols-4" data-testid="services-grid">
              {services.map((service) => {
                const Icon = service.icon;
                return (
                  <article key={service.id} className={`service-card group min-h-[280px] border border-white/10 bg-[#0F0F13]/75 p-7 transition-[border-color,transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[#0047FF]/70 hover:bg-[#12121A] hover:shadow-[0_0_45px_rgba(0,71,255,0.12)] ${service.span}`} data-testid={`service-card-${service.id}`}>
                    <div className="mb-10 flex h-12 w-12 items-center justify-center border border-white/10 bg-white/[0.03] text-[#0047FF] transition-[background-color,transform] duration-300 group-hover:-rotate-3 group-hover:bg-[#0047FF] group-hover:text-white" data-testid={`service-icon-${service.id}`}>
                      <Icon size={26} weight="duotone" />
                    </div>
                    <h3 className="text-2xl font-bold tracking-[-0.035em] text-white" data-testid={`service-title-${service.id}`}>{service.title}</h3>
                    <p className="mt-5 max-w-md text-sm leading-7 text-[#8B8B96]" data-testid={`service-text-${service.id}`}>{service.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="casi-studio" className="px-5 py-24 sm:px-8 sm:py-32 lg:px-12" data-testid="case-studies-section">
          <div className="mx-auto max-w-7xl">
            <div className="mb-16 grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-end" data-testid="case-studies-heading-block">
              <div data-testid="case-studies-title-block">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#0047FF]" data-testid="case-studies-eyebrow">Casi studio</p>
                <h2 className="mt-5 text-4xl font-black leading-[0.95] tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-testid="case-studies-title">
                  Progetti concreti, costruiti sui processi reali.
                </h2>
              </div>
              <p className="max-w-2xl text-base leading-8 text-[#B8B8C2]" data-testid="case-studies-description">
                Alcuni esempi di soluzioni sviluppate per produzione, commerciale, sicurezza e gestione eventi: casi diversi, stesso metodo operativo su misura.
              </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-2 lg:auto-rows-fr" data-testid="case-studies-grid">
              {caseStudies.map((study) => {
                const Icon = study.icon;
                return (
                  <article key={study.id} className="group flex h-full flex-col border border-white/10 bg-[#0F0F13]/80 p-6 transition-[border-color,transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[#0047FF]/70 hover:bg-[#12121A] hover:shadow-[0_0_45px_rgba(0,71,255,0.12)] sm:p-8" data-testid={`case-study-card-${study.id}`}>
                    <div className="mb-7 flex items-start justify-between gap-5" data-testid={`case-study-header-${study.id}`}>
                      <div data-testid={`case-study-heading-${study.id}`}>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#FF2E00]" data-testid={`case-study-sector-${study.id}`}>{study.sector}</p>
                        <h3 className="mt-3 text-3xl font-black tracking-[-0.05em] text-white" data-testid={`case-study-name-${study.id}`}>{study.name}</h3>
                      </div>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-white/10 bg-white/[0.03] text-[#0047FF] transition-[background-color,transform,color] duration-300 group-hover:-rotate-3 group-hover:bg-[#0047FF] group-hover:text-white" data-testid={`case-study-icon-${study.id}`}>
                        <Icon size={26} weight="duotone" />
                      </div>
                    </div>

                    <div className="grid gap-4" data-testid={`case-study-details-${study.id}`}>
                      <div className="border-l border-[#0047FF]/60 pl-4" data-testid={`case-study-problem-block-${study.id}`}>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8B8B96]" data-testid={`case-study-problem-label-${study.id}`}>Problema</p>
                        <p className="mt-2 text-sm leading-7 text-[#D8D8E0]" data-testid={`case-study-problem-${study.id}`}>{study.problem}</p>
                      </div>
                      <div className="border-l border-white/15 pl-4" data-testid={`case-study-solution-block-${study.id}`}>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8B8B96]" data-testid={`case-study-solution-label-${study.id}`}>Soluzione</p>
                        <p className="mt-2 text-sm leading-7 text-[#B8B8C2]" data-testid={`case-study-solution-${study.id}`}>{study.solution}</p>
                      </div>
                      <div className="border border-white/10 bg-[#050505] p-4" data-testid={`case-study-result-block-${study.id}`}>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0047FF]" data-testid={`case-study-result-label-${study.id}`}>Risultato</p>
                        <p className="mt-2 text-sm leading-7 text-white" data-testid={`case-study-result-${study.id}`}>{study.result}</p>
                      </div>
                    </div>

                    <p className="mt-auto pt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid={`case-study-tech-${study.id}`}>
                      {study.tech}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-5 py-24 sm:px-8 sm:py-32 lg:px-12" data-testid="quality-section">
          <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
            <div className="relative min-h-[420px] overflow-hidden border border-white/10 bg-[#0F0F13]" data-testid="quality-image-panel">
              <img src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Server room moderna con illuminazione blu" className="h-full w-full object-cover opacity-75" data-testid="quality-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/35 to-transparent" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 right-0 p-8" data-testid="quality-image-caption">
                <p className="text-xs uppercase tracking-[0.28em] text-white/60">Solidità tecnica</p>
                <p className="mt-3 max-w-sm text-lg font-semibold text-white">Ogni linea di codice è pensata per durare, evolvere e proteggere.</p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2" data-testid="quality-points-grid">
              <div className="border border-white/10 bg-[#0047FF] p-8 text-white" data-testid="quality-point-security">
                <ShieldCheck size={34} weight="fill" />
                <h3 className="mt-8 text-2xl font-bold tracking-[-0.04em]">Sicurezza integrata</h3>
                <p className="mt-4 text-sm leading-7 text-white/80">Proteggiamo dati, accessi e processi fin dalla progettazione.</p>
              </div>
              <div className="border border-white/10 bg-[#0F0F13] p-8" data-testid="quality-point-scalability">
                <Database size={34} weight="duotone" className="text-[#0047FF]" />
                <h3 className="mt-8 text-2xl font-bold tracking-[-0.04em] text-white">Scalabilità reale</h3>
                <p className="mt-4 text-sm leading-7 text-[#8B8B96]">Soluzioni pronte a crescere con utenti, dati e nuove funzionalità.</p>
              </div>
              <div className="border border-white/10 bg-[#0F0F13] p-8 sm:col-span-2" data-testid="quality-point-usability">
                <h3 className="text-3xl font-black tracking-[-0.055em] text-white">La tecnologia deve semplificare, non complicare.</h3>
                <p className="mt-5 max-w-2xl text-base leading-8 text-[#B8B8C2]">Mettiamo l’utente finale al centro: interfacce chiare, flussi intuitivi e strumenti che le persone del team possono davvero usare ogni giorno.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="contatti" className="px-5 py-24 sm:px-8 sm:py-32 lg:px-12" data-testid="contact-section">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div data-testid="contact-info-panel">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#0047FF]" data-testid="contact-eyebrow">Dove contattarci</p>
              <h2 className="mt-5 text-4xl font-black leading-[0.95] tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-testid="contact-title">
                Costruiamo qualcosa di solido insieme.
              </h2>
              <p className="mt-7 max-w-xl text-base leading-8 text-[#B8B8C2]" data-testid="contact-description">
                Raccontaci cosa vuoi migliorare, automatizzare o lanciare. Ti risponderemo con un confronto chiaro sulle possibilità, le priorità e i prossimi passi.
              </p>
              <div className="mt-10 space-y-5" data-testid="contact-details">
                <div className="flex items-start gap-4 text-[#B8B8C2]" data-testid="contact-address">
                  <MapPin size={24} weight="fill" className="mt-1 text-[#FF2E00]" />
                  <span>Legnago (VR), Viale dei Caduti, 109</span>
                </div>
                <a href="mailto:info@zerocinquezero.it" className="flex items-center gap-4 text-[#B8B8C2] transition-colors duration-300 hover:text-white" data-testid="contact-email-link">
                  <EnvelopeSimple size={24} weight="fill" className="text-[#FF2E00]" />
                  <span>info@zerocinquezero.it</span>
                </a>
              </div>
              <div className="mt-10 flex gap-3" data-testid="social-links">
                <a href="https://www.instagram.com/zer0cinquezer0/" target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center border border-white/10 text-[#8B8B96] transition-[border-color,transform,color,background-color] duration-300 hover:-translate-y-1 hover:border-[#0047FF] hover:bg-[#0047FF] hover:text-white" aria-label="Instagram ZeroCinqueZero" data-testid="social-instagram-link"><InstagramLogo size={22} weight="fill" /></a>
                <a href="https://www.facebook.com/zerocinquezero" target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center border border-white/10 text-[#8B8B96] transition-[border-color,transform,color,background-color] duration-300 hover:-translate-y-1 hover:border-[#0047FF] hover:bg-[#0047FF] hover:text-white" aria-label="Facebook ZeroCinqueZero" data-testid="social-facebook-link"><FacebookLogo size={22} weight="fill" /></a>
                <a href="https://www.linkedin.com/company/zerocinquezero/?originalSubdomain=it" target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center border border-white/10 text-[#8B8B96] transition-[border-color,transform,color,background-color] duration-300 hover:-translate-y-1 hover:border-[#0047FF] hover:bg-[#0047FF] hover:text-white" aria-label="LinkedIn ZeroCinqueZero" data-testid="social-linkedin-link"><LinkedinLogo size={22} weight="fill" /></a>
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate className="border border-white/10 bg-[#0F0F13]/80 p-5 shadow-2xl backdrop-blur-xl sm:p-8" data-testid="contact-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <label className="space-y-2" data-testid="contact-name-field">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid="contact-name-label">Nome *</span>
                  <Input required value={formData.name} onChange={(event) => handleFieldChange("name", event.target.value)} placeholder="Il tuo nome" className="h-12 rounded-none border-white/10 bg-[#050505] text-white placeholder:text-[#555560] focus-visible:ring-[#0047FF]" data-testid="contact-form-name" />
                </label>
                <label className="space-y-2" data-testid="contact-email-field">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid="contact-email-label">Email *</span>
                  <Input required type="email" value={formData.email} onChange={(event) => handleFieldChange("email", event.target.value)} placeholder="nome@azienda.it" className="h-12 rounded-none border-white/10 bg-[#050505] text-white placeholder:text-[#555560] focus-visible:ring-[#0047FF]" data-testid="contact-form-email" />
                </label>
                <label className="space-y-2" data-testid="contact-phone-field">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid="contact-phone-label">Telefono</span>
                  <Input value={formData.phone} onChange={(event) => handleFieldChange("phone", event.target.value)} placeholder="+39 000 000 0000" className="h-12 rounded-none border-white/10 bg-[#050505] text-white placeholder:text-[#555560] focus-visible:ring-[#0047FF]" data-testid="contact-form-phone" />
                </label>
                <label className="space-y-2" data-testid="contact-service-field">
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid="contact-service-label">Servizio richiesto</span>
                  <Select value={formData.service_requested} onValueChange={(value) => handleFieldChange("service_requested", value)} required>
                    <SelectTrigger className="h-12 rounded-none border-white/10 bg-[#050505] text-white focus:ring-[#0047FF]" data-testid="contact-form-service-trigger">
                      <SelectValue placeholder="Seleziona" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-white/10 bg-[#0F0F13] text-white" data-testid="contact-form-service-menu">
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id} data-testid={`contact-service-option-${service.id}`}>{service.title}</SelectItem>
                      ))}
                      <SelectItem value="altro" data-testid="contact-service-option-altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
              </div>
              <label className="mt-5 block space-y-2" data-testid="contact-message-field">
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid="contact-message-label">Messaggio</span>
                <Textarea required minLength={10} value={formData.message} onChange={(event) => handleFieldChange("message", event.target.value)} placeholder="Scrivi obiettivo, contesto e tempistiche del progetto..." className="min-h-[170px] rounded-none border-white/10 bg-[#050505] text-white placeholder:text-[#555560] focus-visible:ring-[#0047FF]" data-testid="contact-form-message" />
              </label>
              <label className="mt-5 flex cursor-pointer items-start gap-3 border border-white/10 bg-[#050505] p-4 text-sm leading-6 text-[#B8B8C2] transition-[border-color,background-color] duration-300 hover:border-white/20 hover:bg-white/[0.03]" data-testid="contact-privacy-consent-field">
                <input
                  type="checkbox"
                  checked={formData.privacy_consent}
                  onChange={(event) => handleFieldChange("privacy_consent", event.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#0047FF]"
                  data-testid="contact-form-privacy-consent"
                />
                <span data-testid="contact-privacy-consent-label">
                  Autorizzo il trattamento dei dati personali per essere ricontattato in merito alla richiesta inviata. *
                </span>
              </label>
              <label className="sr-only" aria-hidden="true" data-testid="contact-website-honeypot-field">
                Sito web
                <Input tabIndex={-1} autoComplete="off" value={formData.website} onChange={(event) => handleFieldChange("website", event.target.value)} data-testid="contact-form-website-honeypot" />
              </label>
              {showCaptcha ? (
                <div className="mt-5 border border-white/10 bg-[#050505] p-4" data-testid="contact-captcha-panel">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <label className="w-full space-y-2" data-testid="contact-captcha-field">
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96]" data-testid="contact-captcha-label">Verifica anti-spam</span>
                      <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
                        <div className="flex min-h-12 items-center border border-white/10 bg-white/[0.03] px-4 text-sm font-semibold text-white" data-testid="contact-captcha-question">
                          {captchaChallenge?.question || "Caricamento verifica..."}
                        </div>
                        <Input inputMode="numeric" value={formData.captcha_answer} onChange={(event) => handleFieldChange("captcha_answer", event.target.value)} placeholder="Risposta" className="h-12 rounded-none border-white/10 bg-[#050505] text-white placeholder:text-[#555560] focus-visible:ring-[#0047FF]" data-testid="contact-form-captcha-answer" />
                      </div>
                    </label>
                    <button type="button" onClick={loadCaptcha} className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-[#8B8B96] transition-colors duration-300 hover:text-white sm:pb-3" data-testid="contact-captcha-refresh-button">
                      Cambia domanda
                    </button>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-[#8B8B96]" data-testid="contact-captcha-help-text">
                    Compare solo quando il sistema richiede una verifica aggiuntiva.
                  </p>
                </div>
              ) : (
                <p className="mt-5 border border-white/10 bg-[#050505]/70 p-4 text-xs leading-5 text-[#8B8B96]" data-testid="contact-captcha-adaptive-note">
                  Protezione anti-spam attiva in modo adattivo: la verifica compare solo se necessaria.
                </p>
              )}
              {submissionError && (
                <div className="mt-5 border border-[#FF2E00]/40 bg-[#FF2E00]/10 p-4 text-sm leading-6 text-[#FFD7CC]" data-testid="contact-error-message">
                  {submissionError}
                </div>
              )}
              <Button type="submit" disabled={isSubmitting} className="mt-7 h-13 w-full rounded-none bg-[#0047FF] py-6 text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#336BFF] hover:shadow-[0_0_42px_rgba(0,71,255,0.45)] disabled:translate-y-0 disabled:opacity-70" data-testid="contact-submit-button">
                {isSubmitting ? "Invio in corso..." : "Invia richiesta"}
              </Button>
              {submittedLead && (
                <div className="mt-5 border border-[#0047FF]/40 bg-[#0047FF]/10 p-4 text-sm leading-6 text-[#DDE6FF]" data-testid="contact-success-message">
                  Richiesta ricevuta correttamente. Codice contatto: <strong data-testid="contact-success-id">{submittedLead.id}</strong>
                </div>
              )}
            </form>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 px-5 py-8 text-sm text-[#8B8B96] sm:px-8 lg:px-12" data-testid="site-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-brand">© ZeroCinqueZero — Software house italiana · P.IVA 04830250231</p>
          <div className="flex flex-wrap gap-5" data-testid="footer-links">
            <Link to="/privacy-policy" className="transition-colors duration-300 hover:text-white" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/cookie-policy" className="transition-colors duration-300 hover:text-white" data-testid="footer-cookie-policy-link">Cookie Policy</Link>
            <a href="#top" className="transition-colors duration-300 hover:text-white" data-testid="footer-back-to-top-link">Torna su</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/privacy-policy-ipollini" element={<IPolliniPrivacyPolicyPage />} />
          <Route path="/privacy-policy-salieri-circus" element={<SalieriCircusPrivacyPolicyPage />} />
          <Route path="/privacy-policy-salieri-circus-en" element={<SalieriCircusPrivacyPolicyPageEn />} />
          <Route path="/privacy-policy" element={<SitePrivacyPolicyPage />} />
          <Route path="/privacy-policy-e-tanika" element={<PrivacyPolicyPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
